import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Circle, Clock, MapPin, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { getJobByRefAndToken, STOP_STATUS_META } from "../lib/deliveries";

function fmtTime(iso) { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } }

export default function ClientTracking() {
  const { jobRef, token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let active = true;
    getJobByRefAndToken(jobRef, token)
      .then((d) => { if (!active) return; d ? setState({ loading: false, data: d, error: "" }) : setState({ loading: false, data: null, error: "This tracking link is invalid or has expired." }); })
      .catch((e) => active && setState({ loading: false, data: null, error: e?.message || "Unable to load tracking." }));
    return () => { active = false; };
  }, [jobRef, token]);

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tracking…</div>;

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md">
          <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="font-display text-xl font-extrabold text-[#0f2847]">Tracking unavailable</h1>
          <p className="text-slate-500 mt-2 text-sm">{state.error}</p>
        </div>
      </div>
    );
  }

  const { job, stops } = state.data;
  const delivered = stops.filter((s) => s.status === "delivered").length;
  const remaining = stops.length - delivered;

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo title={`Track ${job.job_reference} | 518 Courier`} description="Live delivery tracking from 518 Courier." path={`/track/${jobRef}/${token}`} noindex />
      <header className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-8 max-w-3xl">
          <div className="font-display font-extrabold text-lg">518 <span className="text-[#f59321]">Courier</span></div>
          <h1 className="mt-3 font-display text-2xl md:text-3xl font-extrabold">{job.client_name || job.job_name}</h1>
          <p className="text-slate-300 text-sm mt-1">{job.job_name}</p>
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
            <div className="bg-white/10 rounded-xl p-3 text-center"><div className="font-display text-2xl font-extrabold">{stops.length}</div><div className="text-[11px] text-slate-300 font-bold">Total stops</div></div>
            <div className="bg-white/10 rounded-xl p-3 text-center"><div className="font-display text-2xl font-extrabold text-[#f59321]">{delivered}</div><div className="text-[11px] text-slate-300 font-bold">Delivered</div></div>
            <div className="bg-white/10 rounded-xl p-3 text-center"><div className="font-display text-2xl font-extrabold">{remaining}</div><div className="text-[11px] text-slate-300 font-bold">Remaining</div></div>
          </div>
          <div className="mt-3 text-xs text-slate-300">Delivery date: {job.job_date}</div>
        </div>
      </header>

      <main className="container-518 py-6 max-w-3xl space-y-2.5" data-testid="client-track-stops">
        {stops.map((s) => {
          const m = STOP_STATUS_META[s.status] || STOP_STATUS_META.pending;
          const done = s.status === "delivered";
          return (
            <div key={s.stop_number} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
              {done ? <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" /> : s.status === "attempted" || s.status === "failed" ? <Clock className="w-6 h-6 text-amber-500 shrink-0" /> : <Circle className="w-6 h-6 text-slate-300 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#0f2847] truncate">{s.recipient_name}{s.company_name ? ` · ${s.company_name}` : ""}</div>
                <div className="text-xs text-slate-500 truncate">{s.city}, {s.state}</div>
                {done && s.delivered_time && <div className="text-xs text-emerald-600 font-semibold mt-0.5">Delivered · {fmtTime(s.delivered_time)}{s.recipient_received_by ? ` · ${s.recipient_received_by}` : ""}</div>}
              </div>
              {(() => {
                const ph = (s.photos && s.photos.length) ? s.photos : (s.photo_url ? [{ url: s.photo_url }] : []);
                if (!ph.length || !done) return null;
                return (
                  <div className="flex gap-1.5 shrink-0">
                    {ph.slice(0, 3).map((p, i) => (
                      <a key={i} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={`POD ${i + 1}`} className="w-12 h-12 rounded-lg object-cover border border-slate-200" /></a>
                    ))}
                    {ph.length > 3 && <span className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">+{ph.length - 3}</span>}
                  </div>
                );
              })()}
              <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>
            </div>
          );
        })}
      </main>
      <footer className="container-518 py-6 max-w-3xl text-center text-xs text-slate-400">
        Powered by 518 Courier · OBLED Corporation d/b/a 518 Courier · (518) 954-4701
      </footer>
    </div>
  );
}
